import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'

import { maxTrialDateISO, minTrialDateISO, type TrialFormValues, trialSchema } from './trialValidation'

import styles from '@/pages/app/Users/styles.module.scss'

import { isApiError } from '@/api'
import { MAX_USER_TRIAL_EXTENSION_DAYS, updateUserTrialEnd } from '@/api/internal'
import Button from '@/components/Button'
import FormField from '@/components/FormField/FormField'
import { useToast } from '@/components/Toast/ToastContext'
import { useAnalytics } from '@/hooks/useAnalytics'
import { EventName } from '@/utils/eventDefinitions'

const UpdateUserTrialTab = () => {
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()

  const {
    control,
    handleSubmit: formHandleSubmit,
    reset,
    formState: { errors },
  } = useForm<TrialFormValues>({
    resolver: zodResolver(trialSchema),
    defaultValues: { user_email: '', trial_end: '' },
  })

  const mutation = useMutation({
    mutationFn: (data: TrialFormValues) => updateUserTrialEnd(data),
    onSuccess: (result) => {
      // The server sets trial_ends_at to 23:59:59 UTC on the chosen day; render the
      // success toast in UTC so admins in any timezone see the same end-of-day value
      // they picked (local time would show the prior day for users west of UTC).
      const dt = new Date(result.data.trial_ends_at * 1000)
      updateToast(`Trial for ${result.data.user_email} now ends ${dt.toUTCString()}`, 'success')
      trackEvent?.(EventName.UpdatedUserTrial, {
        user_id: result.data.user_id,
        trial_ends_at: result.data.trial_ends_at,
      })
      reset()
    },
    onError: (err) => {
      const message = isApiError(err) ? (err.detail ?? err.message) : 'Failed to update trial end date'
      updateToast(message, 'error')
    },
  })

  const onSubmit = formHandleSubmit((data) => mutation.mutate(data))

  return (
    <form className={styles.formSection} onSubmit={onSubmit}>
      <div className={styles.formFields}>
        <h2 className={styles.sectionTitle}>Update User Trial End Date</h2>
        <p className={styles.infoText}>
          Override the personal trial end date for a single user. Capped at {MAX_USER_TRIAL_EXTENSION_DAYS} days from
          today.
        </p>
        <FormField
          name="user_email"
          label="User Email"
          type="email"
          control={control}
          error={errors.user_email}
          placeholder="user@example.com"
        />
        <FormField
          name="trial_end"
          label="New Trial End Date (UTC)"
          type="date"
          control={control}
          error={errors.trial_end}
          hint={`Trial ends at 23:59:59 UTC on the chosen date. Allowed: ${minTrialDateISO()} through ${maxTrialDateISO()}.`}
          placeholder="YYYY-MM-DD"
        />
        <Button
          title="Update Trial End"
          htmlType="submit"
          onClick={() => {
            /* handled by form onSubmit */
          }}
          type="dark"
          isLoading={mutation.isPending}
          isDisabled={mutation.isPending}
        />
      </div>
    </form>
  )
}

export default UpdateUserTrialTab
